import { Origin, Screen, StockPartyClaim_Clicked } from '@stashinvest/product-events-typescript';
import {
	isPartyActive,
	isWithinAfterPartyWindow,
} from 'stash/components/stock-party/partyTiming';
import type { TrackFn } from 'stash/components/stock-party/stockPartyEvents';
import { fetchRequestWithTypes } from './index.js';

export const GET_LAST_AND_CURRENT_OR_NEXT_PARTY_REQUEST =
	'GET_LAST_AND_CURRENT_OR_NEXT_PARTY_REQUEST';
export const GET_LAST_AND_CURRENT_OR_NEXT_PARTY_SUCCESS =
	'GET_LAST_AND_CURRENT_OR_NEXT_PARTY_SUCCESS';
export const GET_LAST_AND_CURRENT_OR_NEXT_PARTY_ERRORED =
	'GET_LAST_AND_CURRENT_OR_NEXT_PARTY_ERRORED';

export const LAST_AND_CURRENT_OR_NEXT_PARTY_PATH =
	'/apis/party/api/v1/parties/last-and-current-or-next';

export const GET_STOCK_PARTY_BY_ID_REQUEST = 'GET_STOCK_PARTY_BY_ID_REQUEST';
export const GET_STOCK_PARTY_BY_ID_SUCCESS = 'GET_STOCK_PARTY_BY_ID_SUCCESS';
export const GET_STOCK_PARTY_BY_ID_ERRORED = 'GET_STOCK_PARTY_BY_ID_ERRORED';

export type StockPartyStock = {
	imageUrl: string;
	ticker: string;
};

export type StockPartySize = {
	dollarAmount: string;
	partierCount: number;
};

export type StockPartyDetail = {
	id: string;
	startTime: string;
	endTime: string;
	name: string;
	stocks: StockPartyStock[] | null;
	size: StockPartySize;
};

export type StockPartyLastAndCurrentOrNextResponse = {
	last: StockPartyDetail | null;
	currentOrNext: StockPartyDetail | null;
};

export const getLastAndCurrentOrNextParty = () =>
	fetchRequestWithTypes({
		types: [
			GET_LAST_AND_CURRENT_OR_NEXT_PARTY_REQUEST,
			GET_LAST_AND_CURRENT_OR_NEXT_PARTY_SUCCESS,
			GET_LAST_AND_CURRENT_OR_NEXT_PARTY_ERRORED,
		],
		request: {
			method: 'GET',
			path: LAST_AND_CURRENT_OR_NEXT_PARTY_PATH,
		},
	});

type StockPartyByIdAction = {
	type: 'FETCH_REQUEST_WITH_TYPES';
	types: [
		typeof GET_STOCK_PARTY_BY_ID_REQUEST,
		typeof GET_STOCK_PARTY_BY_ID_SUCCESS,
		typeof GET_STOCK_PARTY_BY_ID_ERRORED,
	];
	request: {
		method: 'GET';
		path: string;
	};
};

export function getPartyById(partyId: string): StockPartyByIdAction {
	return fetchRequestWithTypes({
		types: [
			GET_STOCK_PARTY_BY_ID_REQUEST,
			GET_STOCK_PARTY_BY_ID_SUCCESS,
			GET_STOCK_PARTY_BY_ID_ERRORED,
		],
		request: {
			method: 'GET',
			path: `/apis/party/api/v1/parties/${encodeURIComponent(partyId)}`,
		},
	});
}

export const POST_REFERRAL_SHARE_LINK_REQUEST = 'POST_REFERRAL_SHARE_LINK_REQUEST';
export const POST_REFERRAL_SHARE_LINK_SUCCESS = 'POST_REFERRAL_SHARE_LINK_SUCCESS';
export const POST_REFERRAL_SHARE_LINK_ERRORED = 'POST_REFERRAL_SHARE_LINK_ERRORED';

export const REFERRAL_SHARE_LINK_PATH =
	'/apis/customers/v1/promotions/referrals/codes';

export const REFERRAL_STOCK_PARTY_TYPE_PLACEMENT = 'referral-stockParty';
export const REFERRAL_STOCK_PARTY_SOURCE = 'stock_party';
export const REFERRAL_STOCK_PARTY_CAMPAIGN_TAG = '';

export const postReferralShareLink = (userId: string) =>
	fetchRequestWithTypes({
		types: [
			POST_REFERRAL_SHARE_LINK_REQUEST,
			POST_REFERRAL_SHARE_LINK_SUCCESS,
			POST_REFERRAL_SHARE_LINK_ERRORED,
		],
		request: {
			method: 'POST',
			path: REFERRAL_SHARE_LINK_PATH,
			body: {
				userId,
				campaignTag: REFERRAL_STOCK_PARTY_CAMPAIGN_TAG,
				typePlacement: REFERRAL_STOCK_PARTY_TYPE_PLACEMENT,
				referralSource: REFERRAL_STOCK_PARTY_SOURCE,
			},
		},
	});

export const POST_STOCK_PARTY_CLAIM_REQUEST = 'POST_STOCK_PARTY_CLAIM_REQUEST';
export const POST_STOCK_PARTY_CLAIM_SUCCESS = 'POST_STOCK_PARTY_CLAIM_SUCCESS';
export const POST_STOCK_PARTY_CLAIM_ERRORED = 'POST_STOCK_PARTY_CLAIM_ERRORED';

const CLAIMED_PARTY_STORAGE_VALUE = '1';

export type StockPartyClaimInput = {
	partyId: string;
	tickerSymbol: string;
	userId: string;
};

export type ClaimedPartyStore = {
	getItem(key: string): string | null;
	setItem(key: string, value: string): void;
};

function stockPartyClaimPath(partyId: string): string {
	return `/apis/party/api/v1/parties/${encodeURIComponent(partyId)}/claims`;
}

function claimedPartyStorageKey(userId: string, partyId: string): string {
	return `stock-party:claimed:${userId}:${partyId}`;
}

type StockPartyClaimAction = {
	type: 'FETCH_REQUEST_WITH_TYPES';
	types: [
		typeof POST_STOCK_PARTY_CLAIM_REQUEST,
		typeof POST_STOCK_PARTY_CLAIM_SUCCESS,
		typeof POST_STOCK_PARTY_CLAIM_ERRORED,
	];
	request: {
		method: 'POST';
		path: string;
		body: {
			user_id: string;
		};
	};
};

export function postStockPartyClaim(partyId: string, userId: string): StockPartyClaimAction {
	return fetchRequestWithTypes({
		types: [
			POST_STOCK_PARTY_CLAIM_REQUEST,
			POST_STOCK_PARTY_CLAIM_SUCCESS,
			POST_STOCK_PARTY_CLAIM_ERRORED,
		],
		request: {
			method: 'POST',
			path: stockPartyClaimPath(partyId),
			body: {
				user_id: userId,
			},
		},
	});
}

export function startStockPartyClaim(
	track: TrackFn,
	input: StockPartyClaimInput
): StockPartyClaimAction {
	track(
		StockPartyClaim_Clicked.v1({
			party_id: input.partyId,
			screen: Screen.stockPartyPreClaim,
			ticker_symbol: input.tickerSymbol,
		})
	);
	return postStockPartyClaim(input.partyId, input.userId);
}

export function isStockPartyClaimed(
	store: ClaimedPartyStore,
	userId: string,
	partyId: string
): boolean {
	return store.getItem(claimedPartyStorageKey(userId, partyId)) === CLAIMED_PARTY_STORAGE_VALUE;
}

export function recordStockPartyClaim(
	store: ClaimedPartyStore,
	input: { userId: string; partyId: string; succeeded: boolean }
): void {
	if (!input.succeeded) {
		return;
	}
	store.setItem(
		claimedPartyStorageKey(input.userId, input.partyId),
		CLAIMED_PARTY_STORAGE_VALUE
	);
}

export function selectPresentedParty(
	payload: StockPartyLastAndCurrentOrNextResponse,
	now: Date
): StockPartyDetail | null {
	const currentOrNext = payload.currentOrNext;
	if (currentOrNext && isPartyActive(currentOrNext, now)) {
		return currentOrNext;
	}
	if (payload.last && isWithinAfterPartyWindow(payload.last, now)) {
		return payload.last;
	}
	return currentOrNext ?? payload.last;
}

const PARTY_ID_UUID_PATTERN =
	/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const ORIGIN_ALIASES: Record<string, StockPartyOrigin | undefined> = {
	REWARDS: Origin.rewardsCenter,
	NOTIFICATION: undefined,
};

export type StockPartyOrigin = (typeof Origin)[keyof typeof Origin];

export type StockPartyEntry = {
	partyId: string | null;
	origin: StockPartyOrigin | undefined;
};

function normalizeOriginToken(raw: string): string {
	return raw.trim().replace(/-/g, '_').toUpperCase();
}

function originFromNormalizedToken(token: string): StockPartyOrigin | undefined {
	if (Object.prototype.hasOwnProperty.call(ORIGIN_ALIASES, token)) {
		return ORIGIN_ALIASES[token];
	}
	const values = Object.values(Origin) as StockPartyOrigin[];
	return values.find((value) => value.toUpperCase() === token);
}

export function parseStockPartyEntry(search: string): StockPartyEntry {
	const query = search.startsWith('?') ? search.slice(1) : search;
	const params = new URLSearchParams(query);
	const rawPartyId = params.get('partyId');
	const partyId =
		rawPartyId && PARTY_ID_UUID_PATTERN.test(rawPartyId) ? rawPartyId : null;
	const origin = params.has('origin')
		? originFromNormalizedToken(normalizeOriginToken(params.get('origin') ?? ''))
		: Origin.deeplink;
	return { partyId, origin };
}
